import { forwardRef } from 'react';
import { cn } from './cn';

/**
 * <Button>
 *
 * Variants:
 *   - primary  (solid azul, accion principal)
 *   - outline  (transparente con borde, accion secundaria)
 *   - ghost    (sin borde, hover sutil — para nav/iconos)
 *   - danger   (rojo solid, eliminar)
 *   - soft     (background tenue, para chips/filtros)
 *
 * Sizes:
 *   - sm | md (default) | lg
 *
 * Slots:
 *   - leftIcon, rightIcon — iconos lucide-react
 *   - children — texto/contenido
 *
 * Sin glow, rounded-lg, transicion suave.
 */
const VARIANTS = {
    primary:
        'bg-primary text-white hover:bg-primary-hover ' +
        'shadow-soft hover:shadow-soft-lg focus-visible:ring-2 focus-visible:ring-primary/40',
    outline:
        'bg-transparent text-cyan2 border border-cyan2/40 hover:border-cyan2 ' +
        'hover:bg-cyan2/5 focus-visible:ring-2 focus-visible:ring-cyan2/30',
    ghost:
        'bg-transparent text-muted hover:text-text hover:bg-surface-2 ' +
        'focus-visible:ring-2 focus-visible:ring-line',
    danger:
        'bg-danger text-white hover:opacity-90 ' +
        'shadow-soft focus-visible:ring-2 focus-visible:ring-danger/40',
    soft:
        'bg-surface-2 text-text hover:bg-line/60 ' +
        'focus-visible:ring-2 focus-visible:ring-line'
};

const SIZES = {
    sm: 'h-8 px-3 text-xs gap-1.5',
    md: 'h-10 px-4 text-sm gap-2',
    lg: 'h-12 px-5 text-base gap-2'
};

const Button = forwardRef(({
    variant = 'primary',
    size = 'md',
    leftIcon,
    rightIcon,
    fullWidth,
    className,
    type = 'button',
    children,
    disabled,
    ...rest
}, ref) => {
    return (
        <button
            ref={ref}
            type={type}
            disabled={disabled}
            className={cn(
                'inline-flex items-center justify-center font-medium rounded-lg',
                'outline-none focus-visible:outline-none transition',
                'disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none',
                VARIANTS[variant] || VARIANTS.primary,
                SIZES[size] || SIZES.md,
                fullWidth && 'w-full',
                className
            )}
            {...rest}
        >
            {leftIcon}
            {children}
            {rightIcon}
        </button>
    );
});

Button.displayName = 'Button';
export default Button;
